import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Table, Spinner, Button, Input, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label } from "reactstrap";
import { AiOutlineMessage, AiOutlineEye, AiOutlineEyeInvisible, AiOutlineSend } from "react-icons/ai";
import axios from "axios";
import { toast } from "react-toastify";
import Pagination from "../../Components/Paginate";

const FeedbackIndex = () => {
  const { AuthReducer: { auth } } = useSelector((state) => state);
  
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState('desc');
  const [paginate, setPaginate] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  const [replyModal, setReplyModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [chatReplies, setChatReplies] = useState({});

  useEffect(() => {
    fetchFeedbacks();
  }, [page, search, sort, paginate]);

  const fetchFeedbacks = () => {
    setLoading(true);
    axios
      .get(`/api/admin/v1/feedback?page=${page + 1}&limit=${paginate}&search=${search}&sort=${sort}`, {
        headers: { Authorization: `Bearer ${auth?.token}` },
      })
      .then((res) => {
        setFeedbacks(res.data.feedback || []);
        setTotalCount(res.data.feedbackCount || 0);
      })
      .catch(() => toast.error("Error fetching feedbacks"))
      .finally(() => setLoading(false));
  };

  const handleReply = (feedback) => {
    setSelectedFeedback(feedback);
    setReplyModal(true);
  };

  const submitReply = () => {
    if (!replyMessage.trim()) {
      toast.error("Please enter a reply message");
      return;
    }

    axios.put(`/api/admin/v1/feedback/${selectedFeedback.id}/reply`, 
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      ).then(() => {
        toast.success("Reply sent successfully");
        setReplyModal(false);
        setReplyMessage("");
        fetchFeedbacks();
      }).catch(() => toast.error("Error sending reply"));
  };

  const toggleConversation = (feedbackId) => {
    setExpandedRows(prev => ({
      ...prev,
      [feedbackId]: !prev[feedbackId]
    }));
  };

  const handleChatReply = (feedbackId) => {
    const message = chatReplies[feedbackId];
    if (!message?.trim()) {
      toast.error("Please enter a reply message");
      return;
    }

    axios.put(`/api/admin/v1/feedback/${feedbackId}/reply`, 
        { message },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      ).then(() => {
        toast.success("Reply sent successfully");
        setChatReplies(prev => ({ ...prev, [feedbackId]: "" }));
        fetchFeedbacks();
      })
      .catch(() => toast.error("Error sending reply"));
  };

  const updateChatReply = (feedbackId, message) => {
    setChatReplies(prev => ({ ...prev, [feedbackId]: message }));
  };

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <h5 className="mb-0 text-white fs-4">Feedback & Suggestions</h5>
        </div>
      </div>

      <div className="bg-white px-3 py-4 rounded-2 my-4">
        <div className="row mb-3">
          <div className="col-md-3">
            <Input 
              size="sm" 
              value={search} 
              placeholder="Search by subject or email" 
              onChange={(e) => setSearch(e.target.value)} 
            />
          </div>
          <div className="col-md-2 offset-md-7">
            <Input type="select" size="sm" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </Input>
          </div>
        </div>

        <Table hover responsive>
          <thead>
            <tr>
              <th>SL. No.</th>
              <th>Subject</th>
              <th>Category</th>
              <th>Name</th>
              <th>Email</th>
              <th>User Type</th>
              <th>Status</th>
              <th>Created At</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9"><Spinner /></td></tr>
            ) : feedbacks.length === 0 ? (
              <tr><td colSpan="9">No feedback found</td></tr>
            ) : (
              feedbacks.map((feedback, index) => (
                <React.Fragment key={feedback.id}>
                  <tr>
                    <td>{index + 1 + page * paginate}</td>
                    <td>{feedback.subject}</td>
                    <td>{feedback.category}</td>
                    <td>{feedback.name}</td>
                    <td>{feedback.email}</td>
                    <td>{feedback.userType}</td>
                    <td>
                      <span className={`badge ${feedback.status ? 'bg-success' : 'bg-warning'}`}>
                        {feedback.status ? 'Resolved' : 'Open'}
                      </span>
                    </td>
                    <td>{new Date(feedback.createdAt).toLocaleDateString()}</td>
                    <td>
                      <Button 
                        color="secondary" 
                        size="sm"
                        className="me-1"
                        onClick={() => toggleConversation(feedback.id)}
                      >
                        {expandedRows[feedback.id] ? <AiOutlineEyeInvisible size={16} /> : <AiOutlineEye size={16} />}
                      </Button>
                      <Button 
                        color="info" 
                        size="sm"
                        onClick={() => handleReply(feedback)}
                      >
                        <AiOutlineMessage size={16} />
                      </Button>
                    </td>
                  </tr>
                  {expandedRows[feedback.id] && (
                    <tr>
                      <td colSpan="9">
                        <div className="p-3 bg-light">
                          <div className="mb-3">
                            <strong>Original Message:</strong>
                            <p className="mt-1">{feedback.message}</p>
                          </div>
                          {feedback.replies && feedback.replies.length > 0 && (
                            <div className="mb-3">
                              <strong className="text-primary mb-2 d-block">💬 Conversation</strong>
                              <div className="chat-container" style={{maxHeight: '300px', overflowY: 'auto'}}>
                                {feedback.replies.map((reply, idx) => (
                                  <div key={idx} className={`d-flex mb-2 ${reply.userType === 'admin' ? 'justify-content-end' : 'justify-content-start'}`}>
                                    <div className={`chat-bubble ${reply.userType === 'admin' ? 'admin-bubble' : 'user-bubble'}`} style={{
                                      maxWidth: '70%',
                                      padding: '8px 12px',
                                      borderRadius: '18px',
                                      backgroundColor: reply.userType === 'admin' ? '#007bff' : '#f8f9fa',
                                      color: reply.userType === 'admin' ? 'white' : '#333',
                                      border: reply.userType === 'admin' ? 'none' : '1px solid #dee2e6'
                                    }}>
                                      <div className="chat-header d-flex justify-content-between align-items-center mb-1">
                                        <small style={{fontSize: '0.75rem', opacity: 0.8}}>
                                          <strong>{reply.userName}</strong>
                                        </small>
                                        <small style={{fontSize: '0.7rem', opacity: 0.7}}>
                                          {new Date(reply.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                        </small>
                                      </div>
                                      <div className="chat-message">{reply.message}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="chat-input-container mt-3 p-3" style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            border: '1px solid #e9ecef'
                          }}>
                            <div className="d-flex align-items-end">
                              <div className="flex-grow-1 me-2">
                                <Input
                                  type="textarea"
                                  rows="2"
                                  placeholder="💭 Type your reply here..."
                                  value={chatReplies[feedback.id] || ""}
                                  onChange={(e) => updateChatReply(feedback.id, e.target.value)}
                                  style={{
                                    border: '1px solid #ced4da',
                                    borderRadius: '20px',
                                    padding: '10px 15px',
                                    resize: 'none',
                                    fontSize: '14px'
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      if (chatReplies[feedback.id]?.trim()) {
                                        handleChatReply(feedback.id);
                                      }
                                    }
                                  }}
                                />
                              </div>
                              <Button
                                color="primary"
                                onClick={() => handleChatReply(feedback.id)}
                                disabled={!chatReplies[feedback.id]?.trim()}
                                style={{
                                  borderRadius: '50%',
                                  width: '45px',
                                  height: '45px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: 'none',
                                  boxShadow: '0 2px 4px rgba(0,123,255,0.3)'
                                }}
                              >
                                <AiOutlineSend size={18} />
                              </Button>
                            </div>
                            <small className="text-muted mt-1 d-block" style={{fontSize: '0.75rem'}}>
                              💡 Press Enter to send, Shift+Enter for new line
                            </small>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </Table>

        <div className="row align-items-center">
          <div className="col-md-6 d-flex">
            <div className="col-md-2">
              <Input
                type="select"
                value={paginate}
                onChange={(e) => setPaginate(e.target.value)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Input>
            </div>
          </div>
          <div className="col-md-6">
            <Pagination
              totalItems={totalCount}
              itemsPerPage={paginate}
              currentPage={page}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>

      {/* Reply Modal */}
      <Modal isOpen={replyModal} toggle={() => setReplyModal(false)}>
        <ModalHeader toggle={() => setReplyModal(false)}>
          Reply to Feedback
        </ModalHeader>
        <ModalBody>
          {selectedFeedback && (
            <div>
              <div className="mb-3">
                <strong>Subject:</strong> {selectedFeedback.subject}
              </div>
              <div className="mb-3">
                <strong>Message:</strong> {selectedFeedback.message}
              </div>
              <FormGroup>
                <Label>Your Reply</Label>
                <Input
                  type="textarea"
                  rows="4"
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Enter your reply..."
                />
              </FormGroup>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={submitReply}>
            Send Reply
          </Button>
          <Button color="secondary" onClick={() => setReplyModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default FeedbackIndex;